import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { Collapse } from 'antd';
import { Contract } from 'entities/Contract';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import s from './PaymentCard.module.scss';
import classNames from 'classnames';
import { BulletsTable } from 'shared/ui/BulletsTable';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useGetCurrencySymbol } from 'app/appState';
import { ReactComponent as CollapseArrow } from 'shared/assets/icons/CollapseArrow.svg';
import { useRole } from 'entities/User';
import { Button } from 'shared/ui/Button';
import { MarkDepositAsRefunded } from 'features/MarkDepositAsRefunded';
import { DepositHistoryModal } from 'features/DepositHistoryModal';
import { DepositPaymentStatus } from 'entities/Deposit';
import { getCurrentDepositStatus } from 'entities/Deposit/utils/helpers/getDepositStatus';

interface DepositTabProps {
  contract: Contract;
}

export const DepositTab: FC<DepositTabProps> = memo((props) => {
  const { contract } = props;
  const { deposit } = contract;

  const { t } = useAppTranslation('contracts');

  const currencySymbol = useGetCurrencySymbol();

  const { isManagement } = useRole();

  const [isDepositHistoryModalOpened, setIsDepositHistoryModalOpened] = useState(false);

  const openDepositHistoryModal = useCallback(() => {
    setIsDepositHistoryModalOpened(true);
  }, []);

  const closeDepositHistoryModal = useCallback(() => {
    setIsDepositHistoryModalOpened(false);
  }, []);

  const depositPaymentStatus = useMemo(() => {
    if (!deposit) {
      return DepositPaymentStatus.NOT_PAID;
    }
    return getCurrentDepositStatus(deposit);
  }, [deposit]);

  const DepositContent = useCallback((): JSX.Element => {
    return (
      <div>
        <BulletsTable
          theme="clear"
          rows={[
            {
              label: t('Base deposit amount'),
              value: `${roundNumber(deposit?.initialDepositAmount)} ${currencySymbol}`,
            },
            {
              label: t('Paid deposit amount'),
              value: `${roundNumber(deposit?.depositAmount)} ${currencySymbol}`,
            },
            {
              label: t('Charged deposit amount'),
              value: `${roundNumber(deposit?.chargedDepositAmount)} ${currencySymbol}`,
            },
            {
              label: t('Cancelled deposit amount'),
              value: `${roundNumber(deposit?.cancelledDepositAmount)} ${currencySymbol}`,
            },
            {
              label: t('Refunded deposit amount'),
              value: `${roundNumber(deposit?.refundedDepositAmount)} ${currencySymbol}`,
            },
          ]}
        />

        <div className="flex flex-col gap-3 mt-6">
          {isManagement && deposit && !deposit?.isRefunded && contract.leaveDate && (
            <>
              <div className="flex flex-col gap-3">
                <MarkDepositAsRefunded
                  depositId={deposit.depositId}
                  cancelledDepositAmount={deposit.cancelledDepositAmount}
                  action={<Button theme="secondary">{t('Mark as refunded')}</Button>}
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end mt-3">
          <Button theme="clear" onClick={openDepositHistoryModal}>
            {t('Show deposit history')}
          </Button>
        </div>
      </div>
    );
  }, [t, deposit, currencySymbol, isManagement, contract.leaveDate, openDepositHistoryModal]);

  return (
    <div>
      {deposit ? (
        <>
          <div className="space-y-3">
            <Collapse
              defaultActiveKey={deposit.depositId}
              className={classNames(s.collapse, {
                [s.refundedOrCredited]: deposit.isRefunded,
              })}
              items={[
                {
                  key: deposit.depositId,
                  label: (
                    <>
                      <div className="flex justify-between font-semibold text-lg">
                        {t('Deposit')} - {t(depositPaymentStatus)}
                      </div>
                    </>
                  ),
                  children: <DepositContent />,
                },
              ]}
              expandIcon={(panelProps) => (
                <CollapseArrow
                  className={classNames('ease-linear duration-200', {
                    '-rotate-90 fill-primaryLight': panelProps.isActive,
                    'rotate-90 fill-secondaryAccent': !panelProps.isActive,
                  })}
                />
              )}
              expandIconPosition="end"
            />
          </div>
          <DepositHistoryModal isOpened={isDepositHistoryModalOpened} depositId={deposit.depositId} onClose={closeDepositHistoryModal} />
        </>
      ) : (
        <div className="font-semibold text-lg pt-7 pb-4 text-center">{t('Not applied')}</div>
      )}
    </div>
  );
});
